import { styled } from '@chronosphereio/chrono-ui';
import { useDebounce } from '@chronosphereio/shared';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import useResizeObserver from 'use-resize-observer';

const Div = styled('div', {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color?: string }>(({ theme, color }) => ({
  ...theme.typography.body1,
  color,
  lineHeight: 'normal',
}));

const isOverflown = ({ clientWidth, clientHeight, scrollWidth, scrollHeight }: HTMLElement) =>
  scrollWidth > clientWidth || scrollHeight > clientHeight;

const resizeText = ({
  element,
  minSize = 10,
  maxSize = 512,
  step = 1,
  unit = 'px',
  maxLines = 0,
}: {
  element: HTMLElement;
  minSize?: number;
  maxSize?: number;
  step?: number;
  unit?: string;
  maxLines?: number;
}) => {
  let fontSize = minSize;
  let overflow = false;

  const parent = element.parentNode as HTMLElement;
  element.style.overflow = 'visible';

  while (!overflow && fontSize < maxSize) {
    element.style.fontSize = `${fontSize}${unit}`;
    if (parent) {
      overflow = isOverflown(parent);

      if (!overflow && maxLines) {
        element.style.height = 'auto';
        const lineHeight = getLineHeightEstimate(element);
        const lineCount = element.clientHeight / lineHeight;

        if (lineCount > maxLines) {
          overflow = true;
        }

        element.style.height = '100%';
      }
    }

    if (!overflow) fontSize += step;
  }

  // revert to last state where no overflow happened
  element.style.fontSize = `${fontSize - step}${unit}`;
  element.style.overflow = 'hidden';
};

const getLineHeightEstimate = (element: HTMLElement) => {
  const spacer = document.createElement('div');
  spacer.innerText = 'a';

  element.appendChild(spacer);

  const height = spacer.clientHeight;
  element.removeChild(spacer);

  return height;
};

const removeLineClamp = (element: HTMLElement) => {
  element.style.height = '100%';
  element.style.display = 'block';
  element.style.webkitBoxOrient = '';
  element.style.webkitLineClamp = '';
  element.style.textOverflow = '';
  element.style.width = 'auto';
};

const setLineClamp = (element: HTMLElement) => {
  const lineHeight = getLineHeightEstimate(element);
  const elementHeight = element.clientHeight;

  if (lineHeight > 0) {
    const lines = Math.floor(elementHeight / lineHeight);
    element.style.webkitLineClamp = `${lines}`;
    element.style.textOverflow = 'ellipsis';
    element.style.webkitBoxOrient = 'vertical';
    element.style.display = '-webkit-box';
    element.style.height = 'auto';
  }

  // If it's just a single line, then make it 100% width and let ellipsis happen naturally
  if (element.clientHeight === lineHeight) {
    element.style.display = 'block';
    element.style.width = '100%';
  }
};

/*
  This component is used to find a font size such that the given text fills up as much of the 
  space it has as possible without overflowing.  If it is still overflowing at the minimum 
  font size, it sets an ellipsis overflow after the number of lines that are displayed
*/
export const FillTextSpace = ({
  children,
  color,
  minFontSize,
  maxFontSize,
  height,
  width,
  maxLines,
  verticallyCenterText = true,
  horizontallyCenterText = false,
  padding = 8,
}: {
  children: ReactNode;
  minFontSize?: number;
  maxFontSize?: number;
  color?: string;
  height: number;
  width: number;
  maxLines?: number;
  verticallyCenterText?: boolean;
  horizontallyCenterText?: boolean;
  padding?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sizeText = useCallback(() => {
    if (ref.current) {
      removeLineClamp(ref.current);
      resizeText({ element: ref.current, minSize: minFontSize, maxSize: maxFontSize, maxLines });
      setLineClamp(ref.current);
    }
  }, [minFontSize, maxFontSize, maxLines]);

  const debouncedSizeText = useDebounce(() => sizeText(), 200);

  // When the container is resized, we want to resize the text
  useResizeObserver({
    ref: containerRef.current,
    onResize: () => {
      debouncedSizeText();
    },
  });

  useEffect(() => {
    sizeText();
  }, [children, sizeText]);

  return (
    /* Outer box sets the padding. Inner div is for checking if overflow is occuring when resizing */
    <div
      style={{
        height,
        width,
        overflow: 'hidden',
        paddingLeft: `${padding}px`,
        paddingRight: `${padding}px`,
      }}
      ref={containerRef}
    >
      <div
        style={{
          display: 'flex',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          alignItems: verticallyCenterText ? 'center' : 'flex-start',
          justifyContent: 'space-around',
          flexWrap: 'wrap',
          textAlign: horizontallyCenterText ? 'center' : 'left',
        }}
      >
        <Div color={color} ref={ref}>
          {children}
        </Div>
      </div>
    </div>
  );
};
